import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";

const emojis = [
    "🐶", "🚗", "🦄", "🎈", "🚀", "🐘", "🍎", "🏀", "📕", "⚽",
    "🍕", "🎸", "🚢", "🐱", "🌞", "🍔", "🚲", "🏕", "📡", "🔑",
    "🐳", "🌵", "🎠", "🍩", "🧀", "🚂", "🐙", "🦆", "🏖", "📖",
    "🍉", "🌊", "🎻", "🌈", "🍟", "⚡", "🎯", "🐟", "🚜", "🦅",
    "📱", "📚", "🥕", "🦖", "🎃", "🐬", "🎁", "🍇", "🚀", "🛶",
    "🦜", "🏹", "🐧", "🦊", "🏰", "🚦", "🎠", "🌼", "💎", "🗻",
    "🦄", "📌", "🥦", "🎷", "🔬", "🐼", "📎", "🦕", "🍵", "⚔️",
    "🦉", "🌙", "🎭", "🏜", "📀", "🦢", "🐎", "🧊", "🎇", "🏄",
    "🚁", "🦋", "🦔", "🍪", "🛰", "🐝", "🎤", "🦑", "🏎", "🍍"
];

const MoreOrLessGame: React.FC = () => {
    const [setA, setSetA] = useState<string[]>([]);
    const [setB, setSetB] = useState<string[]>([]);
    const [questionType, setQuestionType] = useState<string>("more"); // "more" or "less"
    const [currentEmoji, setCurrentEmoji] = useState<string>("🍎");
    const [correctAnswer, setCorrectAnswer] = useState<string>("");
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewQuestion();
        document.title = "More and Less Worksheets for UKG | Quantity Comparison Activities";
    }, []);

    const generateNewQuestion = () => {
        let sizeA, sizeB;

        // Ensure set sizes are different
        do {
            sizeA = Math.floor(Math.random() * 6) + 1; // Between 1 and 6
            sizeB = Math.floor(Math.random() * 6) + 1; // Between 1 and 6
        } while (sizeA === sizeB); // Keep generating until they are different

        // Choose a random emoji for the question
        const selectedEmoji = emojis[Math.floor(Math.random() * emojis.length)];
        setCurrentEmoji(selectedEmoji);

        // Create emoji sets
        const newSetA = new Array(sizeA).fill(selectedEmoji);
        const newSetB = new Array(sizeB).fill(selectedEmoji);

        // Decide if the question is "more" or "less"
        const isMoreQuestion = Math.random() > 0.5;
        setQuestionType(isMoreQuestion ? "more" : "less");

        // Determine the correct answer
        const correct = sizeA > sizeB ? "A" : "B";
        setCorrectAnswer(isMoreQuestion ? correct : correct === "A" ? "B" : "A");

        setSetA(newSetA);
        setSetB(newSetB);
        setMessage("");
    };

    const checkAnswer = (selected: string) => {
        if (selected === correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                generateNewQuestion()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setShowImage(false);
            setMessage("❌ Oops! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Teach UKG children the concept of more and less using fun visuals and counting exercises. Download free printable worksheets to build strong number sense." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/pre-math-concepts/more-less" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container game-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>More and Less</h1>

                                    <p>
                                        Comparing <strong>more</strong> and <strong>less</strong> is one of the first steps in building early math skills. In UKG, this concept helps children understand quantity, numbers, and logical thinking. At Mama’s Guidance, our <strong>more and less worksheets</strong> are designed to make learning engaging and visual.
                                    </p>

                                    <h2>🔍 What Are More and Less?</h2>
                                    <p>
                                        <strong>More</strong> means a larger number or greater quantity, while <strong>less</strong> means a smaller number or lower quantity.
                                    </p>

                                    <ul>
                                        <li>A plate with 5 apples has <strong>more</strong> than a plate with 2 apples.</li>
                                        <li>If you have 3 pencils and your friend has 6, your friend has <strong>more</strong>.</li>
                                        <li>A basket with 1 ball has <strong>less</strong> than one with 4 balls.</li>
                                    </ul>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Visually compare quantities using <strong>“more” and “less”</strong></li>
                                        <li>Build early number sense and counting skills</li>
                                        <li>Use comparison vocabulary accurately</li>
                                        <li>Apply the concept in real-life situations</li>
                                    </ul>

                                    <h2>🖍️ What’s in the Worksheets?</h2>
                                    <ul>
                                        <li>Circle the group that has more or less items</li>
                                        <li>Match objects with correct quantity labels</li>
                                        <li>Color the group with more or fewer items</li>
                                        <li>Fill in the blanks: <strong>“Which has more?”</strong></li>
                                        <li>Hands-on counting and comparison activities</li>
                                    </ul>

                                    <h2>👩‍🏫 Tips for Parents and Teachers</h2>
                                    <ul>
                                        <li>Use toys, fruits, and blocks to show different quantities</li>
                                        <li>Ask simple questions: <strong>“Do you want more juice or less?”</strong></li>
                                        <li>Play sorting games to separate groups based on number</li>
                                        <li>Use story-based learning to compare sets (<strong>e.g., more cookies, fewer toys</strong>)</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Long - Short</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Heavy - Light</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Front - Back</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        Encourage children to describe what they see: <strong>“This group has more toys,”</strong> or <strong>“That box has less candy.”</strong> It strengthens both math and language skills.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>More and Less</h1>

                                <p>
                                    Comparing <strong>more</strong> and <strong>less</strong> is one of the first steps in building early math skills. In UKG, this concept helps children understand quantity, numbers, and logical thinking. At Mama’s Guidance, our <strong>more and less worksheets</strong> are designed to make learning engaging and visual.
                                </p>

                                <h2>🔍 What Are More and Less?</h2>
                                <p>
                                    <strong>More</strong> means a larger number or greater quantity, while <strong>less</strong> means a smaller number or lower quantity.
                                </p>

                                <ul>
                                    <li>A plate with 5 apples has <strong>more</strong> than a plate with 2 apples.</li>
                                    <li>If you have 3 pencils and your friend has 6, your friend has <strong>more</strong>.</li>
                                    <li>A basket with 1 ball has <strong>less</strong> than one with 4 balls.</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Visually compare quantities using <strong>“more” and “less”</strong></li>
                                    <li>Build early number sense and counting skills</li>
                                    <li>Use comparison vocabulary accurately</li>
                                    <li>Apply the concept in real-life situations</li>
                                </ul>

                                <h2>🖍️ What’s in the Worksheets?</h2>
                                <ul>
                                    <li>Circle the group that has more or less items</li>
                                    <li>Match objects with correct quantity labels</li>
                                    <li>Color the group with more or fewer items</li>
                                    <li>Fill in the blanks: <strong>“Which has more?”</strong></li>
                                    <li>Hands-on counting and comparison activities</li>
                                </ul>

                                <h2>👩‍🏫 Tips for Parents and Teachers</h2>
                                <ul>
                                    <li>Use toys, fruits, and blocks to show different quantities</li>
                                    <li>Ask simple questions: <strong>“Do you want more juice or less?”</strong></li>
                                    <li>Play sorting games to separate groups based on number</li>
                                    <li>Use story-based learning to compare sets (<strong>e.g., more cookies, fewer toys</strong>)</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Long - Short</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Heavy - Light</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Front - Back</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Encourage children to describe what they see: <strong>“This group has more toys,”</strong> or <strong>“That box has less candy.”</strong> It strengthens both math and language skills.
                                </p>
                            </article>
                            <h2>Which set has {questionType} {currentEmoji}?</h2>

                            <div style={{ display: "flex", justifyContent: "center", margin: "20px 0", width: "100%" }}>
                                <div className="setDiv" onClick={() => checkAnswer("A")} style={{ cursor: "pointer", display: "grid" }}>
                                    <span>Set A</span>
                                    <span>{setA.join(" ")}</span>
                                </div>
                                <div className="setBorder"></div>
                                <div className="setDiv" onClick={() => checkAnswer("B")} style={{ cursor: "pointer", display: "grid" }}>
                                    <span>Set B</span>
                                    <span>{setB.join(" ")}</span>
                                </div>
                            </div>

                            <p style={{ fontSize: "20px" }}>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default MoreOrLessGame;
